import React from 'react';

import http from 'api/http';
import { manufacturerAPI } from 'api/requests';

import Store from '../../../registerIDBWorker';
import WithUser from 'HOC/User';
import { ROLES } from 'constants/roles';

import Header from './Header';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import Footer from './Footer';
import Button, { buttonColor, buttonType } from 'Components/Button';
import Modal from 'Components/Modal';

const SEARCHABLE_FIELDS = ['name', 'code'];

@WithUser
export default class InspectorDashboardScene extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rowsCount: 10,
      activePage: 1,
      sortedBy: 'name',
      sortOrder: 1,
      inspectors: [],
      filterState: {active: true},
      additionalFilterState: {},
      searchString: '',
      isModalOpen: '',
      requestInProcess: false,
      listRequestInProcess: true,
      selectedYear: '',
      currentYear: 0,
    };

    this.allInspectors = [];
    this.previousRole = null;

    this.isOilDashboard = (props.user.getActiveRole() === ROLES.OIL_MANUFACTURER.serverName ||
      props.user.getActiveRole() === ROLES.OIL_BRANDER.serverName);
    this.isWithPaymentStatus = false;
    this.isWithCertificationStatus = false;

    props.user.selectInspector(null)();
  }

  componentDidMount() {
    const {user} = this.props;

    this.previousRole = user.getActiveRole();
    this.loadFromStore();
    this.updateListData();
  }

  componentDidUpdate() {
    const {user} = this.props;

    if (this.previousRole !== user.getActiveRole()) {
      this.previousRole = user.getActiveRole();

      this.setState({inspectors: [], listRequestInProcess: true}, () => {
        this.loadFromStore();
        this.updateListData();
      });
    }
  }

  updateListData = () => {
    const {user} = this.props;
    const requestedRole = user.getActiveRole();

    http.get(manufacturerAPI.inspectorsList(requestedRole)).then(({ data }) => {
      Store.insert(`${requestedRole}_inspectors`, data).then(() => {
        this.setState({ listRequestInProcess: false });
        this.loadFromStore();
      });
    });
  };

  loadFromStore = () => {
    const {user} = this.props;

    Store.select(`${user.getActiveRole()}_inspectors`)
      .then(data => {
        this.allInspectors = data;
        this.filterList();
        this.sortList();
      });
  };

  handleSortClick = field => () => {
    this.setState(prevState => {
      return {
        sortedBy: field,
        sortOrder: prevState.sortedBy === field ? -prevState.sortOrder : prevState.sortOrder,
        activePage: 1,
      };
    }, this.sortList);
  };

  sortList = () => {
    const {sortedBy, sortOrder} = this.state;
    let {inspectors} = this.state;

    inspectors.sort((a, b) => {
      switch (sortedBy) {
        case 'active':
          return a.active > b.active ? sortOrder : -sortOrder;
        default:
          return a[sortedBy].toLowerCase().replace(/\s+/g, ' ') > b[sortedBy].toLowerCase().replace(/\s+/g, ' ') ? sortOrder : -sortOrder;
      }
    });
    this.setState({inspectors});
  };

  handleRowSelect = (rowsCountSelected, totalRowsCount) => {
    const rowsCount = (rowsCountSelected && rowsCountSelected < totalRowsCount) ? rowsCountSelected : 0;

    this.setState({rowsCount, activePage: 1});
  };

  handlePaginationClick = activePage => {
    this.setState({activePage});
  };

  handleSearchStringInput = value => {
    const {user} = this.props;
    this.setState({searchString:value, activePage:1}, () => {
      this.filterList();
      user.updateURLSearchString({searchString:value});
    });
  };

  filterList = () => {
    const {searchString} = this.state;
    let inspectors = this.allInspectors;

    const doesSearchableFieldsContainSearchString = (element) => {
      for (let searchableFieldIndex in SEARCHABLE_FIELDS) {
        if (element[SEARCHABLE_FIELDS[searchableFieldIndex]]) {
          if (element[SEARCHABLE_FIELDS[searchableFieldIndex]].toString().toLowerCase()
            .includes(searchString.toLowerCase())) return true;
        }
      }
      return false;
    };

    if (searchString) inspectors = inspectors.filter(element => doesSearchableFieldsContainSearchString(element));

    this.setState({inspectors}, this.sortList);
  };

  render() {
    const {
      inspectors,
      rowsCount,
      activePage,
      sortedBy,
      sortOrder,
      searchString,
      isModalOpen,
      requestInProcess,
      listRequestInProcess
    } = this.state;
    const {user} = this.props;

    const tableColumnsWidths = this.isOilDashboard ?
      {
        code: '_width_pc_10',
        allRoles: '_width_pc_20',
        isCertified: '_width_pc_12',
        active: '_width_pc_12',
        actions: '_width_pc_12',
        isPaid: '_width_pc_12',
      }
      :
      {
        code: '_width_pc_10',
        isCertified: '_width_pc_12',
        active: '_width_pc_12',
        actions: '_width_pc_12',
        isPaid: '_width_pc_12',
      };

    return (
      <React.Fragment>
        <section className='scene__scene-wrapper'>
          <section className='scene__content-wrapper list-scene__content-wrapper dashboard-scene'>
            <div className='scene__scene-header list-scene__scene-header'>
              {`${user.getRoleName().split(' ')[0]} Inspectors`}
            </div>
            <Header
              user={user}
              rowsCount={rowsCount}
              outCount={inspectors.length}
              handleRowCountSelect={this.handleRowSelect}
              listRequestInProcess={listRequestInProcess}
              searchString={searchString}
              handleSearchStringInput={this.handleSearchStringInput}
            />
            <TableHeader
              user={user}
              handleSortClick={this.handleSortClick}
              order={sortOrder === 1}
              sortedBy={sortedBy}
            />
            <TableBody
              noEntrysForThisInspector={!this.allInspectors.length}
              listRequestInProcess={listRequestInProcess}
              inspectors={inspectors}
              user={user}
              rowsCount={rowsCount}
              activePage={activePage}
              isOilDashboard={this.isOilDashboard}
              tableColumnsWidths={tableColumnsWidths}
              handleStatusChangeClick={this.handleStatusChangeClick}
              sortedBy={sortedBy}
              isWithPaymentStatus={this.isWithPaymentStatus}
              isWithCertificationStatus={this.isWithCertificationStatus}
            />
            <Footer
              handleClick={this.handlePaginationClick}
              handleSelect={this.handleRowSelect}
              activePage={activePage}
              rowsCount={rowsCount}
              outCount={inspectors.length}
            />
          </section>
        </section>

        <Modal isOpen={isModalOpen}>
          <div className='ect-modal-header'>Confirmation</div>
          <div className='ect-modal-body'>{this.$modalBody}</div>
          <div className='ect-modal-footer'>
            <Button
              label='No'
              buttonClassName='_width_135 _height_40'
              onClick={this._closeModal}
              color={buttonColor.gray}
              type={buttonType.secondary}
            />
            <Button
              label='Yes'
              disabledWithSpinner={requestInProcess}
              buttonClassName='_width_135 _height_40'
              onClick={this.$confirmModalAction}
            />
          </div>
        </Modal>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/Scenes/InspectorDashboardScene/index.jsx